// Product media viewer: Fit media to the screen and the lightbox of the Media
// block, shared by the product page, Featured product and Quick view.
(function () {
  'use strict';

  if (window.AxiomProductMediaViewer) return;

  function motionDuration(name, fallback) {
    return window.Axiom && typeof window.Axiom.getMotionDuration === 'function'
      ? window.Axiom.getMotionDuration(name, fallback)
      : parseFloat(fallback) || 420;
  }

  function motionEase(name, fallback) {
    return window.Axiom && typeof window.Axiom.getMotionEase === 'function'
      ? window.Axiom.getMotionEase(name, fallback)
      : fallback;
  }

  function layoutFlags(gallery) {
    var has = function (name) { return gallery.classList.contains('media-gallery--' + name); };
    return {
      featuredStage: has('featured_stage'),
      oneImage: has('stacked_full_image'),
      twoRows: has('two_rows'),
      verticalSplit: has('vertical_split_image'),
      // Layouts from Treble (see product-gallery.liquid): hero and carousel on a
      // Slide base, grid, feature and collage on a 1 full 2 split base.
      hero: has('hero'),
      carousel: has('carousel'),
      feature: has('feature'),
      collage: has('collage'),
      grid: has('grid')
    };
  }

  function listeners() {
    var cleanups = [];
    return {
      on: function (target, eventName, handler, options) {
        if (!target || typeof target.addEventListener !== 'function') return;
        target.addEventListener(eventName, handler, options);
        cleanups.push(function () {
          target.removeEventListener(eventName, handler, options);
        });
      },
      add: function (cleanup) {
        cleanups.push(cleanup);
      },
      dispose: function () {
        while (cleanups.length) cleanups.pop()();
      }
    };
  }

  // ── FIT TO SCREEN ─────────────────────────────
  // Media block > Fit media to the screen (desktop).
  // - page (product page): the main media fills the room from its top to the
  //   bottom of the screen. The room is measured at the top of the page and once
  //   the gallery sticks (Slide and 2 column), after the header and page padding
  //   scroll away, so the media grows into the freed room and shrinks back when
  //   scrolled up. Scrolling only toggles a class.
  // - section (Featured product): the main media fits the screen below the header.
  // - dialog (Quick view): the main media fits the window below its title bar.
  // options.layoutRoot: the element whose children are the gallery and the
  // details columns (page). options.dialog: the Quick view window (dialog).
  function fitScreen(gallery, options) {
    options = options || {};
    var surface = options.surface || 'page';
    var layoutRoot = options.layoutRoot || gallery.parentElement;
    var flags = layoutFlags(gallery);
    var mediaStage = gallery.querySelector('[data-media-stage]');
    var mainWrap = gallery.querySelector('[data-zoom-wrap]');
    var mediaItemEls = Array.prototype.slice.call(gallery.querySelectorAll('[data-media-item]'));
    var splitShowcaseRows = Array.prototype.slice.call(gallery.querySelectorAll('[data-split-showcase-row]'));
    var desktop = window.matchMedia('(min-width: 1024px)');
    var gap = 16;
    var state = { threshold: Infinity, grow: false };
    var frame = null;
    var events = listeners();

    function update() {
      gallery.classList.toggle('is-fit-stuck', state.grow && window.scrollY > state.threshold);
    }

    function firstShownMedia() {
      return mediaItemEls.find(function (element) { return !element.hidden; }) || null;
    }

    // The frame that fills the room: Slide's, Carousel's and the tiles' main
    // view, both rows of 2 column, the whole collage, the large media beside
    // the column, or the first media of 1 full 2 split and the grid.
    function fitTarget() {
      if (flags.feature) {
        var lead = firstShownMedia();
        return lead && lead.firstElementChild;
      }
      if (flags.collage) return mediaStage;
      if (flags.grid) return firstShownMedia();
      if (flags.verticalSplit) {
        var first = mediaStage && mediaStage.firstElementChild;
        return first && !first.hidden ? first : null;
      }
      if (flags.featuredStage) return gallery.querySelector('[data-split-showcase]');
      return mainWrap;
    }

    function headerOffset() {
      var header = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--axiom-header-height'));
      return (Number.isFinite(header) ? header : 68) + gap;
    }

    // Room for a frame that starts `offset` below the top of what holds it and
    // keeps `below` under it on screen.
    function containedRoom(offset, below) {
      if (surface === 'dialog') {
        var dialog = options.dialog || gallery.closest('.quick-view-modal__shell, [role="dialog"]');
        var head = dialog && dialog.querySelector('[data-quick-view-head], .quick-view-modal__head');
        var maxHeight = dialog ? parseFloat(window.getComputedStyle(dialog).maxHeight) : NaN;
        if (!Number.isFinite(maxHeight)) maxHeight = window.innerHeight * 0.92;
        maxHeight = Math.min(maxHeight, window.innerHeight);
        return maxHeight - (head ? head.offsetHeight : 0) - offset - below;
      }
      return window.innerHeight - headerOffset() - offset - gap - below;
    }

    function measure() {
      frame = null;
      var target = fitTarget();
      if (!desktop.matches || !target) {
        state.grow = false;
        update();
        return;
      }
      if (flags.featuredStage) {
        var rows = splitShowcaseRows.filter(function (row) { return !row.hidden; }).length;
        gallery.style.setProperty('--fit-rows', String(Math.max(rows, 1)));
      }
      var galleryStyle = window.getComputedStyle(gallery);
      var galleryBox = gallery.getBoundingClientRect();
      // The column's large media moves while it sticks: measure its item.
      var targetBox = (flags.feature ? target.parentElement : target).getBoundingClientRect();
      var offset = targetBox.top - galleryBox.top;
      // Slide, Carousel, 2 column and the collage keep what sits under their
      // frame on screen (outside thumbnails, the progress line); the tiles and
      // the longer layouts may scroll below.
      var below = flags.verticalSplit || flags.hero ? 0 : Math.max(galleryBox.bottom - targetBox.bottom, 0);

      if (surface !== 'page') {
        var room = Math.max(containedRoom(offset, below), 320);
        gallery.style.setProperty('--fit-h-top', Math.round(room) + 'px');
        gallery.style.setProperty('--fit-h-stuck', Math.round(room) + 'px');
        state.grow = false;
        update();
        markReady();
        return;
      }

      var sticky = galleryStyle.position === 'sticky';
      var stickyTop = sticky ? parseFloat(galleryStyle.top) || 0 : 0;
      // Where the gallery starts in the page when not stuck, where the frame sits
      // in it, and what the gallery keeps under the frame (outside thumbnails).
      var top = layoutRoot.getBoundingClientRect().top + window.scrollY +
        parseFloat(window.getComputedStyle(layoutRoot).paddingTop) + parseFloat(galleryStyle.marginTop);
      var topRoom = Math.max(window.innerHeight - top - offset - gap - below, 320);
      var stuckRoom = Math.max(window.innerHeight - stickyTop - offset - gap - below, topRoom);
      gallery.style.setProperty('--fit-h-top', Math.round(topRoom) + 'px');
      gallery.style.setProperty('--fit-h-stuck', Math.round(stuckRoom) + 'px');
      if (flags.feature) {
        // The large media sticks inside its column, which scrolls past it.
        var mediaTop = parseFloat(window.getComputedStyle(target).top) || 0;
        stuckRoom = Math.max(window.innerHeight - mediaTop - gap, topRoom);
        gallery.style.setProperty('--fit-h-stuck', Math.round(stuckRoom) + 'px');
        state.grow = mediaStage.offsetHeight > stuckRoom + gap;
        state.threshold = top + offset - mediaTop;
        update();
        markReady();
        return;
      }
      // Growing only helps while the gallery sticks: shorter than the screen and
      // than the tallest details column.
      var tallest = 0;
      Array.prototype.forEach.call(layoutRoot.children, function (child) {
        if (child !== gallery) tallest = Math.max(tallest, child.offsetHeight);
      });
      var grown = gallery.offsetHeight + (gallery.classList.contains('is-fit-stuck') ? 0 : stuckRoom - topRoom);
      state.grow = sticky && tallest > grown && grown + stickyTop <= window.innerHeight;
      state.threshold = top - stickyTop;
      update();
      markReady();
    }

    // Size changes animate once the first measurement is in place.
    function markReady() {
      if (gallery.classList.contains('is-fit-ready')) return;
      window.requestAnimationFrame(function () {
        gallery.classList.add('is-fit-ready');
      });
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(measure);
    }

    if (surface === 'page') events.on(window, 'scroll', update, { passive: true });
    events.on(window, 'resize', schedule, { passive: true });
    events.on(window, 'load', schedule, { once: true });
    if (surface === 'page' && typeof ResizeObserver !== 'undefined') {
      // Every column: the details can grow without the gallery changing.
      var fitObserver = new ResizeObserver(schedule);
      Array.prototype.forEach.call(layoutRoot.children, function (child) {
        fitObserver.observe(child);
      });
      events.add(function () {
        fitObserver.disconnect();
      });
    }
    events.add(function () {
      if (frame) window.cancelAnimationFrame(frame);
      frame = null;
    });
    measure();

    return {
      measure: schedule,
      dispose: events.dispose
    };
  }

  // ── LIGHTBOX ──────────────────────────────────
  // Opens the gallery's images full screen. options:
  // - lightbox: the lightbox element (snippet product-lightbox).
  // - getItems(): the gallery items (product-gallery-controller items).
  // - getIndex(): the active item's index.
  // - setMedia(index, options): makes an item active in the gallery.
  function lightbox(gallery, options) {
    options = options || {};
    var box = options.lightbox;
    var mainWrap = gallery.querySelector('[data-zoom-wrap]');
    var lightboxImg = box && box.querySelector('[data-lightbox-img]');
    var lightboxFrame = box && box.querySelector('.lightbox-frame');
    var lightboxControls = box && box.querySelector('.lightbox-controls');
    if (!box || !mainWrap || !lightboxImg || !lightboxFrame) return null;

    var flags = layoutFlags(gallery);
    var canOpenLightbox = mainWrap.dataset.zoomEnabled === 'true';
    var events = listeners();
    var lightboxAnimation = null;
    var lightboxClosing = false;
    var lightboxImageRequestId = 0;
    var lightboxDecodedSources = new Map();
    var lightboxHoverZoomEnabled = box.dataset.hoverZoom === 'true';
    var lightboxHoverZoomQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    var configuredLightboxZoomScale = Number(box.dataset.zoomScale) / 100;
    var lightboxZoomScale = Number.isFinite(configuredLightboxZoomScale)
      ? Math.min(5, Math.max(1.1, configuredLightboxZoomScale))
      : 2;
    box.style.setProperty('--lightbox-zoom-scale', lightboxZoomScale);

    // Smooth-scroll and opening transforms create containing blocks for fixed overlays.
    var originalParent = box.parentNode;
    if (box.parentNode !== document.body) document.body.appendChild(box);

    function items() {
      return options.getItems ? options.getItems() || [] : [];
    }

    function currentIndex() {
      return options.getIndex ? options.getIndex() : 0;
    }

    function setMedia(index, setOptions) {
      if (options.setMedia) options.setMedia(index, setOptions || {});
    }

    function isOpen() {
      return box.getAttribute('aria-hidden') === 'false';
    }

    function getLightboxPreviewSrc(item) {
      if (!item || item.type !== 'image' || !item.mediaEl) return '';
      return item.mediaEl.currentSrc || item.mediaEl.src || item.lightbox || '';
    }

    function getLightboxImageSrc(item) {
      if (!item || item.type !== 'image' || !item.mediaEl) return '';
      return item.lightbox || getLightboxPreviewSrc(item);
    }

    function findImageMediaIndex(direction) {
      var list = items();
      if (!list.length) return -1;
      var step = direction < 0 ? -1 : 1;
      var current = currentIndex();
      for (var offset = 1; offset <= list.length; offset++) {
        var idx = (current + (offset * step) + list.length) % list.length;
        if (list[idx] && !list[idx].variantHidden && list[idx].type === 'image') return idx;
      }
      return -1;
    }

    function canUseLightboxHoverZoom() {
      return lightboxHoverZoomEnabled && lightboxHoverZoomQuery.matches && box.classList.contains('is-ready');
    }

    function updateLightboxZoomOrigin(event) {
      if (!canUseLightboxHoverZoom()) return;
      var left = parseFloat(lightboxImg.style.left);
      var top = parseFloat(lightboxImg.style.top);
      var width = parseFloat(lightboxImg.style.width);
      var height = parseFloat(lightboxImg.style.height);
      if (![left, top, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return;
      var isInsideImage = event.clientX >= left
        && event.clientX <= left + width
        && event.clientY >= top
        && event.clientY <= top + height;
      if (!isInsideImage || (lightboxControls && lightboxControls.contains(event.target))) {
        resetLightboxZoom(false);
        return;
      }
      var originX = Math.min(100, Math.max(0, ((event.clientX - left) / width) * 100));
      var originY = Math.min(100, Math.max(0, ((event.clientY - top) / height) * 100));
      lightboxImg.style.transformOrigin = originX + '% ' + originY + '%';
      box.classList.add('is-zoomed');
    }

    function resetLightboxZoom(immediate) {
      box.classList.remove('is-zoomed');
      if (!immediate) return;
      lightboxImg.style.transformOrigin = '50% 50%';
      lightboxImg.style.transition = 'none';
      lightboxImg.style.transform = 'none';
      void lightboxImg.offsetWidth;
      lightboxImg.style.transition = '';
      lightboxImg.style.transform = '';
    }

    function getLightboxImageDimensions(item) {
      if (!item || !item.mediaEl) return { width: 0, height: 0, ratio: 1 };
      var width = Number(item.mediaEl.getAttribute('width')) || item.mediaEl.naturalWidth || 0;
      var height = Number(item.mediaEl.getAttribute('height')) || item.mediaEl.naturalHeight || 0;
      return {
        width: width,
        height: height,
        ratio: width > 0 && height > 0 ? width / height : 1
      };
    }

    function getLightboxTargetRect(imageRatio) {
      var viewportW = window.innerWidth;
      var viewportH = window.innerHeight;
      var maxW = viewportW * 0.92;
      var maxH = viewportH * 0.88;
      var ratio = Number.isFinite(imageRatio) && imageRatio > 0 ? imageRatio : 1;
      var width = maxW;
      var height = width / ratio;
      if (height > maxH) {
        height = maxH;
        width = height * ratio;
      }
      return {
        left: (viewportW - width) / 2,
        top: (viewportH - height) / 2,
        width: width,
        height: height
      };
    }

    function setLightboxRect(rect) {
      lightboxImg.style.left = rect.left + 'px';
      lightboxImg.style.top = rect.top + 'px';
      lightboxImg.style.width = rect.width + 'px';
      lightboxImg.style.height = rect.height + 'px';
    }

    function decodeLightboxSource(src) {
      if (!src) return Promise.resolve(null);
      if (lightboxDecodedSources.has(src)) return lightboxDecodedSources.get(src);

      var image = new Image();
      image.decoding = 'async';
      image.src = src;
      var decodedSource = image.decode().then(function () {
        return src;
      }).catch(function () {
        return null;
      });
      lightboxDecodedSources.set(src, decodedSource);
      return decodedSource;
    }

    function setLightboxContent(item, displaySrc) {
      if (!displaySrc) return null;
      var dimensions = getLightboxImageDimensions(item);

      lightboxImg.removeAttribute('srcset');
      lightboxImg.removeAttribute('sizes');
      lightboxImg.src = displaySrc;
      lightboxImg.alt = item.mediaEl.alt || '';
      if (dimensions.width > 0 && dimensions.height > 0) {
        lightboxImg.width = Math.round(dimensions.width);
        lightboxImg.height = Math.round(dimensions.height);
      }

      return getLightboxTargetRect(dimensions.ratio);
    }

    function upgradeLightboxImage(item, requestId) {
      var previewSrc = getLightboxPreviewSrc(item);
      var lightboxSrc = getLightboxImageSrc(item);
      if (!lightboxSrc || lightboxSrc === previewSrc) return;

      decodeLightboxSource(lightboxSrc).then(function (decodedSrc) {
        if (!decodedSrc || requestId !== lightboxImageRequestId) return;
        if (!isOpen() || items()[currentIndex()] !== item) return;
        lightboxImg.src = decodedSrc;
      });
    }

    function preloadAdjacentLightboxPreviews() {
      [-1, 1].forEach(function (direction) {
        var idx = findImageMediaIndex(direction);
        if (idx < 0) return;
        decodeLightboxSource(getLightboxPreviewSrc(items()[idx]));
      });
    }

    // Layouts that show several media grow from (and return to) the active one.
    function getLightboxSourceElement() {
      if (flags.oneImage || flags.twoRows || flags.verticalSplit || flags.carousel) {
        var activeItem = items()[currentIndex()];
        var element = activeItem && activeItem.element;
        if (element) {
          return element.querySelector('.media-main') || element;
        }
      }
      return mainWrap;
    }

    function openLightbox() {
      if (!canOpenLightbox) return;
      var item = items()[currentIndex()] || {};
      if (item.type !== 'image' || !item.mediaEl || lightboxClosing) return;
      var previewSrc = getLightboxPreviewSrc(item);
      if (!previewSrc) return;
      resetLightboxZoom(true);
      var requestId = ++lightboxImageRequestId;
      if (lightboxAnimation) {
        lightboxAnimation.cancel();
        lightboxAnimation = null;
      }

      decodeLightboxSource(previewSrc).then(function (decodedSrc) {
        if (!decodedSrc || requestId !== lightboxImageRequestId || lightboxClosing) return;
        var target = setLightboxContent(item, decodedSrc);
        if (!target) return;
        mainWrap.classList.add('is-lightbox-opening');
        box.classList.remove('is-closing');
        var sourceEl = getLightboxSourceElement();
        var start = sourceEl.getBoundingClientRect();

        box.classList.remove('is-ready');
        lightboxImg.style.left = start.left + 'px';
        lightboxImg.style.top = start.top + 'px';
        lightboxImg.style.width = start.width + 'px';
        lightboxImg.style.height = start.height + 'px';
        lightboxImg.style.opacity = '1';
        box.setAttribute('aria-hidden', 'false');
        box.removeAttribute('inert');
        if (window.Axiom && window.Axiom.lockPageScroll) window.Axiom.lockPageScroll(box);
        preloadAdjacentLightboxPreviews();

        lightboxAnimation = lightboxImg.animate([
          {
            left: start.left + 'px',
            top: start.top + 'px',
            width: start.width + 'px',
            height: start.height + 'px'
          },
          {
            left: target.left + 'px',
            top: target.top + 'px',
            width: target.width + 'px',
            height: target.height + 'px'
          }
        ], {
          duration: motionDuration('--motion-duration', '420ms'),
          easing: motionEase('--motion-ease', 'cubic-bezier(0.22, 1, 0.36, 1)'),
          fill: 'forwards'
        });

        lightboxAnimation.onfinish = function () {
          setLightboxRect(target);
          box.classList.add('is-ready');
          mainWrap.classList.remove('is-lightbox-opening');
          lightboxAnimation = null;
          upgradeLightboxImage(item, requestId);
        };
        lightboxAnimation.oncancel = function () {
          mainWrap.classList.remove('is-lightbox-opening');
          lightboxAnimation = null;
        };
      });
    }

    function navigateLightbox(direction) {
      var idx = findImageMediaIndex(direction);
      if (idx < 0) return;
      var item = items()[idx];
      var previewSrc = getLightboxPreviewSrc(item);
      if (!previewSrc) return;
      resetLightboxZoom(true);
      var requestId = ++lightboxImageRequestId;

      decodeLightboxSource(previewSrc).then(function (decodedSrc) {
        if (!decodedSrc || requestId !== lightboxImageRequestId || lightboxClosing) return;
        var target = setLightboxContent(item, decodedSrc);
        if (!target) return;
        setMedia(idx, { scrollMedia: false });
        setLightboxRect(target);
        upgradeLightboxImage(item, requestId);
        preloadAdjacentLightboxPreviews();
      });
    }

    function resizeLightboxImage() {
      if (!isOpen() || lightboxClosing) return;
      var item = items()[currentIndex()];
      if (!item || item.type !== 'image') return;
      resetLightboxZoom(true);
      setLightboxRect(getLightboxTargetRect(getLightboxImageDimensions(item).ratio));
    }

    function finishClose() {
      box.setAttribute('aria-hidden', 'true');
      box.setAttribute('inert', '');
      box.classList.remove('is-closing');
      lightboxImg.style.opacity = '';
      lightboxImg.style.left = '';
      lightboxImg.style.top = '';
      lightboxImg.style.width = '';
      lightboxImg.style.height = '';
      if (window.Axiom && window.Axiom.unlockPageScroll) window.Axiom.unlockPageScroll(box);
      lightboxAnimation = null;
      lightboxClosing = false;
    }

    function closeLightbox() {
      if (box.getAttribute('aria-hidden') === 'true' || lightboxClosing) return;
      lightboxImageRequestId += 1;
      if (lightboxAnimation) {
        lightboxAnimation.cancel();
        lightboxAnimation = null;
      }
      resetLightboxZoom(true);
      lightboxClosing = true;
      box.classList.remove('is-ready');
      box.classList.add('is-closing');

      var currentRect = lightboxImg.getBoundingClientRect();
      var sourceEl = getLightboxSourceElement();
      var endRect = sourceEl.getBoundingClientRect();

      lightboxAnimation = lightboxImg.animate([
        {
          left: currentRect.left + 'px',
          top: currentRect.top + 'px',
          width: currentRect.width + 'px',
          height: currentRect.height + 'px'
        },
        {
          left: endRect.left + 'px',
          top: endRect.top + 'px',
          width: endRect.width + 'px',
          height: endRect.height + 'px'
        }
      ], {
        duration: motionDuration('--motion-duration', '420ms'),
        easing: motionEase('--motion-ease', 'cubic-bezier(0.22, 1, 0.36, 1)'),
        fill: 'forwards'
      });

      lightboxAnimation.onfinish = finishClose;
      lightboxAnimation.oncancel = function () {
        box.classList.remove('is-closing');
        lightboxAnimation = null;
        lightboxClosing = false;
      };
    }

    events.on(mainWrap, 'click', function (event) {
      if (!canOpenLightbox) return;
      if (event.target.closest('[data-media-prev], [data-media-next], [data-split-row-nav]')) return;
      if (window.AxiomProductMedia && window.AxiomProductMedia.isControlTarget(event.target)) return;
      var clickedMediaItem = event.target.closest('[data-media-item]');
      if (!clickedMediaItem || clickedMediaItem.dataset.mediaType !== 'image') return;
      // Layouts that show several media open the one that was clicked.
      var clickedIndex = items().findIndex(function (item) { return item.element === clickedMediaItem; });
      if (clickedIndex >= 0 && clickedIndex !== currentIndex()) {
        setMedia(clickedIndex, { scrollMedia: false, scrollThumb: false });
      }
      openLightbox();
    });
    events.on(box.querySelector('[data-lightbox-close]'), 'click', closeLightbox);
    events.on(box.querySelector('[data-lightbox-prev]'), 'click', function () {
      navigateLightbox(-1);
    });
    events.on(box.querySelector('[data-lightbox-next]'), 'click', function () {
      navigateLightbox(1);
    });
    events.on(box, 'pointermove', updateLightboxZoomOrigin, { passive: true });
    events.on(box, 'pointerleave', function () {
      resetLightboxZoom(false);
    });
    events.on(window, 'resize', resizeLightboxImage);
    events.on(box, 'click', function (e) {
      if (e.target === box || e.target === lightboxFrame) closeLightbox();
    });
    // Capture: Escape closes the lightbox only, not the Quick view under it.
    events.on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) {
        e.stopPropagation();
        closeLightbox();
      }
    }, true);

    return {
      element: box,
      open: openLightbox,
      close: closeLightbox,
      isOpen: isOpen,
      dispose: function () {
        events.dispose();
        if (lightboxAnimation) lightboxAnimation.cancel();
        if (isOpen() || lightboxClosing) finishClose();
        mainWrap.classList.remove('is-lightbox-opening');
        if (options.removeOnDispose) {
          box.remove();
        } else if (originalParent && box.parentNode !== originalParent) {
          originalParent.appendChild(box);
        }
      }
    };
  }

  window.AxiomProductMediaViewer = {
    fitScreen: fitScreen,
    lightbox: lightbox
  };
})();
